import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  Badge,
  ThemeSwitcher,
} from '@lognai/synapse-design-system-v2';
import { Lock, LogOut, Building, UserPlus, Check } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { ChangePasswordModal } from './ChangePasswordModal';

interface UserMenuProps {
  user: {
    fullName?: string;
    name?: string;
    email?: string;
  } | null;
}

function getInitials(name: string | undefined): string {
  if (!name) return 'U';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function UserMenu({ user }: UserMenuProps) {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const {
    logout,
    company,
    accessibleCompanies,
    activeCompanyId,
    allAccounts,
    switchCompany,
    switchAccount,
    addAccount,
    logoutAllAccounts,
  } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    setShowChangePassword(true);
  };

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId !== activeCompanyId) {
      await switchCompany(companyId);
    }
  };

  const handleSwitchAccount = (email: string) => {
    if (email !== user?.email) {
      switchAccount(email);
    }
  };

  const displayName = user?.fullName || user?.name || 'User';
  const initials = getInitials(displayName);
  const hasMultipleCompanies = accessibleCompanies.length > 1;
  const hasMultipleAccounts = allAccounts.length > 1;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 rounded-lg p-2">
            <Avatar>
              <AvatarFallback className="bg-primary text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start text-left">
              <span className="text-sm font-medium">{displayName}</span>
              <span className="text-xs text-muted-foreground">{user?.email || ''}</span>
            </div>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72">
          {/* Current Account Info */}
          <div className="px-3 py-2">
            <div className="font-medium">{displayName}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
            {company && (
              <div className="text-xs text-muted-foreground mt-1">
                {company.name}
              </div>
            )}
          </div>

          <DropdownMenuSeparator />

          {/* Company Switcher */}
          {hasMultipleCompanies && (
            <>
              <DropdownMenuLabel>Switch Company</DropdownMenuLabel>
              {accessibleCompanies.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => handleSwitchCompany(company.id)}
                  disabled={company.id === activeCompanyId}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <Building className="mr-2 h-4 w-4" />
                    <span>{company.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {company.isPrimary && (
                      <Badge variant="outline" className="text-xs">Primary</Badge>
                    )}
                    {company.id === activeCompanyId && (
                      <Check className="h-4 w-4" />
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Other Accounts */}
          {hasMultipleAccounts && (
            <>
              <DropdownMenuLabel>Other Accounts</DropdownMenuLabel>
              {allAccounts
                .filter((acc) => acc.email !== user?.email)
                .map((account) => (
                  <DropdownMenuItem
                    key={account.email}
                    onClick={() => handleSwitchAccount(account.email)}
                    className="flex items-center"
                  >
                    <Avatar className="mr-2 h-6 w-6">
                      <AvatarFallback className="text-xs">
                        {getInitials(account.user.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-sm">{account.user.fullName || 'User'}</span>
                      <span className="text-xs text-muted-foreground">{account.email}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Actions */}
          <DropdownMenuItem onClick={addAccount}>
            <UserPlus className="mr-2 h-4 w-4" />
            <span>Add another account</span>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleChangePassword}>
            <Lock className="mr-2 h-4 w-4" />
            <span>Change Password</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Theme */}
          <div className="px-3 py-2">
            <p className="text-xs text-muted-foreground mb-2">Theme</p>
            <ThemeSwitcher className="w-full justify-around" />
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>

          {hasMultipleAccounts && (
            <DropdownMenuItem onClick={logoutAllAccounts}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out of all accounts</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </>
  );
}
